import type { AuthUser } from '@yoreflix/core/api/types'
import { userCan } from '../auth/permissions'

/**
 * The provider dashboard's information architecture, as data — the same contract as admin's and
 * for the same reason: the side menu renders it, the router resolves a route from it, and the
 * permission filter decides what an account may see. One declaration, so the three cannot drift.
 *
 * ★★ THE PARTITION IS IN THE MENU, not only in the data (dashboard-portal-plan.md §6). "What you
 * sent us" and "how your titles are performing" are two entries and never one list. The sets are
 * genuinely different and not 1:1 in either direction — one delivery becomes seven episodes; a
 * master, its captions and its artwork are three deliveries of one title. Fusing them fails as a
 * TRUST problem, not a cosmetic one: anything delivered but not yet published shows zero views,
 * which reads as "nobody watched my film" rather than "it isn't out yet".
 *
 * ★ The two menu groups ARE this portal's two base sources (David, 2026-07-23): Performance is
 * fed by CONTENT — the catalogue attributed to this org, and the watch data carrying the same
 * OrganizationKey — and Your account is fed by SUPPLIED CONTENT, the admin-authored ledger.
 *
 * `ready` is honest bookkeeping, as in admin. The portal ships in phases (D0 → D3); the menu
 * shows the whole shape from the start and an unbuilt entry renders visibly disabled. Hiding
 * entries until built makes the menu change shape under the user across deploys — worse for a
 * supplier than for staff, who can at least ask us what happened without writing an email.
 */

export type Phase = 'D0' | 'D1' | 'D2' | 'D3'

export interface NavItem {
  /** Stable id — also the hash route (`#/<id>`), so a section has one name, not two. */
  id: string
  label: string
  /** The permission required to SEE this entry (the server independently enforces its own). */
  permission: string
  /** Which build phase delivers it — the source of `ready`, and what the menu tooltips say. */
  phase: Phase
  /** Built and navigable? False renders it disabled. */
  ready: boolean
}

export interface NavGroup {
  title: string
  items: NavItem[]
}

/**
 * ⚠ Every entry is gated `provider.*`, which is the role's only grant (config/roles.php), so
 * these are not distinct gates today. They are named separately so that narrowing a supplier's
 * access later is a role change rather than a portal rewrite.
 */
export const NAV: NavGroup[] = [
  {
    title: 'Performance',
    items: [
      { id: 'overview', label: 'Overview', permission: 'provider.*', phase: 'D3', ready: true },
      { id: 'titles', label: 'Your titles', permission: 'provider.*', phase: 'D3', ready: true },
    ],
  },
  {
    title: 'Your account',
    items: [
      // ⚠ The delivery ledger. It NEVER carries analytics columns — see the partition note.
      { id: 'supplied', label: 'What you sent us', permission: 'provider.*', phase: 'D1', ready: true },
      { id: 'profile', label: 'Organization', permission: 'provider.*', phase: 'D1', ready: true },
    ],
  },
]

/** Every item, flattened — for route resolution. */
export function allItems(nav: NavGroup[] = NAV): NavItem[] {
  return nav.flatMap((g) => g.items)
}

/**
 * The nav this user may see: items filtered by permission, then any group left empty dropped —
 * an "Administration" heading over nothing is worse than no heading.
 */
export function visibleNav(user: AuthUser | null, nav: NavGroup[] = NAV): NavGroup[] {
  return nav
    .map((group) => ({ ...group, items: group.items.filter((item) => userCan(user, item.permission)) }))
    .filter((group) => group.items.length > 0)
}

/**
 * Resolve a hash route to the item it names — but only if the user may see it AND it is built.
 * Returns null otherwise, which the shell turns into a fallback to the landing item. This is why
 * route resolution goes through the permission filter: a hand-typed `#/reports` must not render
 * a page a content-manager cannot have.
 */
export function resolveRoute(route: string, user: AuthUser | null, nav: NavGroup[] = NAV): NavItem | null {
  // ⚠ The query suffix is stripped: the period rides in the hash so a view is bookmarkable
  // (`#/overview?period=previous-quarter`), and without this the whole string is compared to the
  // id, so every shared link falls back to the landing screen.
  const id = route.replace(/^#?\/?/, '').split('?')[0]
  const item = allItems(visibleNav(user, nav)).find((i) => i.id === id)
  return item?.ready ? item : null
}

/** Where a user lands with no route (or a bad one): the first thing they can actually open. */
export function landingItem(user: AuthUser | null, nav: NavGroup[] = NAV): NavItem | null {
  return allItems(visibleNav(user, nav)).find((i) => i.ready) ?? null
}
